import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Plus, AlertCircle } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { 
  FORMULARY_CATEGORIES,
  FORMULARY_FORMS,
  FORMULARY_ROUTES,
  adminApi
} from '@/services/adminService';
import { selectAdminPharmacies } from '@/store/slices/adminSlice';

interface PharmacyCreateData {
  selected: boolean;
  price: number;
}

interface MedicationCreateData {
  id: string;
  name: string;
  amount: number;
  unit: string;
}

interface FormData {
  category: string;
  form: string;
  route: string;
  quantity: number;
  unit: string;
  medications: MedicationCreateData[];
  pharmacies: Record<string, PharmacyCreateData>;
}

interface CreateFormularyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: () => void;
}

const CreateFormularyModal: React.FC<CreateFormularyModalProps> = ({
  isOpen,
  onClose,
  onCreate
}) => {
  // Get all available pharmacies from Redux cache
  const allPharmacies = useSelector(selectAdminPharmacies);
  
  // Initialize form data
  const getInitialFormData = (): FormData => {
    // Initialize pharmacy data for all available pharmacies (none selected by default)
    const pharmacyData: Record<string, PharmacyCreateData> = {};
    allPharmacies.forEach(pharmacy => {
      pharmacyData[pharmacy.id] = {
        selected: false,
        price: 100.00 // Default price
      };
    });

    return {
      category: '',
      form: '',
      route: '',
      quantity: 0,
      unit: '',
      medications: [
        {
          id: 'med-1',
          name: '',
          amount: 0,
          unit: ''
        }
      ],
      pharmacies: pharmacyData
    };
  };

  const [formData, setFormData] = useState<FormData>(getInitialFormData());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form data when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setFormData(getInitialFormData());
      setError(null);
    }
  }, [isOpen]);

  const handleInputChange = (field: keyof Omit<FormData, 'pharmacies' | 'medications'>, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(null);
  };

  const handlePharmacyChange = (pharmacyId: string, field: keyof PharmacyCreateData, value: boolean | number) => {
    setFormData(prev => ({
      ...prev,
      pharmacies: {
        ...prev.pharmacies,
        [pharmacyId]: {
          ...prev.pharmacies[pharmacyId],
          [field]: value
        }
      }
    }));
    if (error) setError(null);
  };

  const handleMedicationChange = (index: number, field: keyof Omit<MedicationCreateData, 'id'>, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      medications: prev.medications.map((med, i) => 
        i === index 
          ? { ...med, [field]: value }
          : med
      )
    }));
    if (error) setError(null);
  };

  const addMedication = () => {
    setFormData(prev => ({
      ...prev,
      medications: [
        ...prev.medications,
        {
          id: `med-${prev.medications.length + 1}`,
          name: '',
          amount: 0,
          unit: ''
        }
      ]
    }));
  };

  const removeMedication = (index: number) => {
    if (formData.medications.length > 1) {
      setFormData(prev => ({
        ...prev,
        medications: prev.medications.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.category || !formData.form || !formData.route) {
      setError('Please fill in all required fields');
      return;
    }

    // Validate medications
    const hasValidMedication = formData.medications.some(med => med.name.trim() !== '');
    if (!hasValidMedication) {
      setError('Please add at least one medication with a name');
      return;
    }

    // Validate that all selected pharmacies have prices
    const selectedPharmacies = Object.entries(formData.pharmacies).filter(([_, data]) => data.selected);
    const missingPrices = selectedPharmacies.filter(([_, data]) => !data.price || data.price <= 0);
    
    if (missingPrices.length > 0) {
      setError('All selected pharmacies must have a price greater than $0');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Create formulary with basic data
      const createData = {
        name: formData.medications[0]?.name || 'New Formulary',
        category: formData.category,
        form: formData.form,
        route: formData.route,
        quantity: formData.quantity,
        unit: formData.unit
      };

      const newFormulary = await adminApi.createFormulary(createData);

      // Add pharmacy relationships for selected pharmacies
      const selectedPharmacyEntries = Object.entries(formData.pharmacies).filter(([_, data]) => data.selected);
      
      for (const [pharmacyId, data] of selectedPharmacyEntries) {
        try {
          await adminApi.addPharmacyToFormulary(
            pharmacyId,
            newFormulary.id,
            data.price
          );
        } catch (err) {
          console.warn(`Failed to add pharmacy ${pharmacyId} to formulary:`, err);
        }
      }

      // Success - close modal and refresh data
      onCreate();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create formulary');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData(getInitialFormData());
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto border-l-4 border-l-emerald-500">
        <DialogHeader>
          <DialogTitle className="font-syne font-medium text-vitl-navy">
            Create New Formulary
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-lato font-medium text-vitl-dark/70 mb-2 block">
                Category *
              </Label>
              <Select value={formData.category} onValueChange={(value) => handleInputChange('category', value)}>
                <SelectTrigger className="font-lato">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {FORMULARY_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm font-lato font-medium text-vitl-dark/70 mb-2 block">
                Form *
              </Label>
              <Select value={formData.form} onValueChange={(value) => handleInputChange('form', value)}>
                <SelectTrigger className="font-lato">
                  <SelectValue placeholder="Select form" />
                </SelectTrigger>
                <SelectContent>
                  {FORMULARY_FORMS.map((form) => (
                    <SelectItem key={form.value} value={form.value}>{form.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-sm font-lato font-medium text-vitl-dark/70 mb-2 block">
              Route *
            </Label>
            <Select value={formData.route} onValueChange={(value) => handleInputChange('route', value)}>
              <SelectTrigger className="font-lato">
                <SelectValue placeholder="Select route" />
              </SelectTrigger>
              <SelectContent>
                {FORMULARY_ROUTES.map((route) => (
                  <SelectItem key={route.value} value={route.value}>{route.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-lato font-medium text-vitl-dark/70 mb-2 block">
                Quantity
              </Label>
              <Input
                type="number"
                value={formData.quantity}
                onChange={(e) => handleInputChange('quantity', parseFloat(e.target.value) || 0)}
                placeholder="0"
                className="font-lato"
                min="0"
                step="0.1"
              />
            </div>

            <div>
              <Label className="text-sm font-lato font-medium text-vitl-dark/70 mb-2 block">
                Unit
              </Label>
              <Input
                value={formData.unit}
                onChange={(e) => handleInputChange('unit', e.target.value)}
                placeholder="mg, ml, etc."
                className="font-lato"
              />
            </div>
          </div>

          {/* Medications Section */}
          <div className="space-y-4 border-t pt-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-lato font-medium text-vitl-dark/70">
                Medications *
              </h4>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addMedication}
                className="text-blue-600 border-blue-200 hover:bg-blue-50"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Medication
              </Button>
            </div>
            <div className="space-y-3">
              {formData.medications.map((medication, index) => (
                <div key={medication.id} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="grid grid-cols-1 gap-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-lato font-medium text-vitl-dark/70">
                        Medication Name *
                      </Label>
                      {formData.medications.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeMedication(index)}
                          className="text-red-600 hover:bg-red-50 h-6 w-6 p-0"
                        >
                          ×
                        </Button>
                      )}
                    </div>
                    <Input
                      value={medication.name}
                      onChange={(e) => handleMedicationChange(index, 'name', e.target.value)}
                      className="font-lato"
                      placeholder="Medication name"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-sm font-lato font-medium text-vitl-dark/70 mb-2 block">
                          Amount
                        </Label>
                        <Input
                          type="number"
                          value={medication.amount}
                          onChange={(e) => handleMedicationChange(index, 'amount', parseFloat(e.target.value) || 0)}
                          className="font-lato"
                          min="0"
                          step="0.1"
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-lato font-medium text-vitl-dark/70 mb-2 block">
                          Unit
                        </Label>
                        <Input
                          value={medication.unit}
                          onChange={(e) => handleMedicationChange(index, 'unit', e.target.value)}
                          className="font-lato"
                          placeholder="mg, ml, etc."
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pharmacy Management Section */}
          <div className="space-y-4 border-t pt-4">
            <div>
              <h4 className="text-sm font-lato font-medium text-vitl-dark/70 mb-3">
                Pharmacy Availability & Pricing
              </h4>
              <div className="space-y-3">
                {allPharmacies.map((pharmacy) => {
                  const isSelected = formData.pharmacies[pharmacy.id]?.selected || false;
                  const currentPrice = formData.pharmacies[pharmacy.id]?.price || 100.00;
                  
                  return (
                    <div key={pharmacy.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <Switch
                          id={`pharmacy-${pharmacy.id}`}
                          checked={isSelected}
                          onCheckedChange={(checked) => handlePharmacyChange(pharmacy.id, 'selected', checked)}
                        />
                        <div className="flex-1">
                          <label
                            htmlFor={`pharmacy-${pharmacy.id}`}
                            className="text-sm font-lato font-medium text-vitl-navy cursor-pointer"
                          >
                            {pharmacy.name}
                          </label>
                          <p className="text-xs text-vitl-dark/50 font-lato">
                            {isSelected ? 'Will be available' : 'Not selected'}
                          </p>
                        </div>
                      </div>
                      
                      {isSelected && (
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-lato text-vitl-dark/70">$</span>
                          <Input
                            type="number"
                            value={currentPrice}
                            onChange={(e) => {
                              const price = parseFloat(e.target.value) || 0;
                              handlePharmacyChange(pharmacy.id, 'price', price);
                            }}
                            className="w-24 h-8 text-sm font-lato"
                            min="0"
                            step="0.01"
                            placeholder="Required"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center">
                <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
                <p className="text-red-700 font-lato text-sm">{error}</p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              className="font-lato"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-lato font-medium"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Formulary
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateFormularyModal;